import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Trophy, ArrowLeft, Shield, Phone, Mail, Globe } from 'lucide-react';
import { SEO } from '../components/SEO';

function RefundPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <SEO
        title="Refund Policy"
        description="Read Malda Turf's refund and rescheduling policy for online turf bookings."
        path="/refund"
      />
      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A]/95 backdrop-blur-sm border-b border-[#1A1A1A]">
        <div className="max-w-4xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
            <div className="w-8 h-8 bg-[#16A34A] flex items-center justify-center flex-shrink-0" style={{ borderRadius: '50px' }}>
              <Trophy className="h-3.5 w-3.5 text-white" strokeWidth={2.5} />
            </div>
            <span className="text-base font-bold tracking-tight font-heading">Malda Turf</span>
          </div>
          <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-xs text-neutral-500 hover:text-white transition-colors font-body">
            <ArrowLeft className="h-3.5 w-3.5" /> Back
          </button>
        </div>
      </nav>

      {/* Content */}
      <div className="pt-28 pb-20 px-6">
        <div className="max-w-3xl mx-auto">
          {/* Header */}
          <div className="mb-12">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-[#16A34A]/10 border border-[#16A34A]/20 flex items-center justify-center">
                <Shield className="h-5 w-5 text-[#16A34A]" />
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-bold font-heading">Refund Policy</h1>
                <p className="text-[11px] text-neutral-500 font-body mt-0.5">Last updated: April 2026</p>
              </div>
            </div>
            <p className="text-sm text-neutral-400 font-body leading-relaxed">
              Welcome to <a href="https://maldaturf.in" className="text-[#16A34A] hover:underline">Malda Turf</a>. By making a booking on our platform, you agree to the following terms regarding refunds and cancellations.
            </p>
          </div>

          {/* Sections */}
          <div className="space-y-8">

            {/* Section 1 */}
            <div className="bg-[#111111] border border-[#1A1A1A] rounded-2xl p-6 md:p-8" data-testid="refund-section-1">
              <div className="flex items-start gap-3 mb-4">
                <span className="flex-shrink-0 w-7 h-7 rounded-lg bg-[#16A34A]/10 border border-[#16A34A]/20 flex items-center justify-center text-[11px] font-bold text-[#16A34A]">1</span>
                <h2 className="text-base md:text-lg font-bold font-heading pt-0.5">No Refund Policy</h2>
              </div>
              <div className="pl-10">
                <p className="text-sm text-neutral-400 font-body leading-relaxed mb-4">
                  At Malda Turf, we maintain a strict <strong className="text-white">No Refund</strong> policy. Once a slot is booked and the payment is confirmed, no refunds will be issued under any circumstances, including but not limited to:
                </p>
                <ul className="space-y-2.5">
                  {[
                    'The player/team failing to reach the venue on time.',
                    'Inability to play due to personal reasons, health issues, or injuries.',
                    'Reduction in the number of players.',
                    'Mistakes made during the booking process (e.g., selecting the wrong date or time).',
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-2.5">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#16A34A]/50 flex-shrink-0 mt-1.5"></div>
                      <span className="text-sm text-neutral-400 font-body leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Section 2 */}
            <div className="bg-[#111111] border border-[#1A1A1A] rounded-2xl p-6 md:p-8" data-testid="refund-section-2">
              <div className="flex items-start gap-3 mb-4">
                <span className="flex-shrink-0 w-7 h-7 rounded-lg bg-[#16A34A]/10 border border-[#16A34A]/20 flex items-center justify-center text-[11px] font-bold text-[#16A34A]">2</span>
                <h2 className="text-base md:text-lg font-bold font-heading pt-0.5">User-Side Rescheduling</h2>
              </div>
              <div className="pl-10">
                <p className="text-sm text-neutral-400 font-body leading-relaxed">
                  We do not offer a self-service or requested rescheduling option for any personal reasons. If a player is unable to attend their booked slot, the booking will be considered a <strong className="text-white">"No-Show"</strong>, and the amount paid will be forfeited.
                </p>
              </div>
            </div>

            {/* Section 3 */}
            <div className="bg-[#111111] border border-[#1A1A1A] rounded-2xl p-6 md:p-8" data-testid="refund-section-3">
              <div className="flex items-start gap-3 mb-4">
                <span className="flex-shrink-0 w-7 h-7 rounded-lg bg-[#16A34A]/10 border border-[#16A34A]/20 flex items-center justify-center text-[11px] font-bold text-[#16A34A]">3</span>
                <h2 className="text-base md:text-lg font-bold font-heading pt-0.5">Turf-Side Issues (Weather & Technical)</h2>
              </div>
              <div className="pl-10">
                <p className="text-sm text-neutral-400 font-body leading-relaxed mb-4">
                  The only exception to our policy is if the turf is deemed unsuitable for play due to:
                </p>
                <ul className="space-y-2.5 mb-4">
                  {[
                    'Extreme weather conditions or natural disasters.',
                    'Technical failures (e.g., floodlight failure, maintenance issues).',
                    'Unforeseen government-mandated closures.',
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-2.5">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#16A34A]/50 flex-shrink-0 mt-1.5"></div>
                      <span className="text-sm text-neutral-400 font-body leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
                <div className="bg-[#0A0A0A] border border-[#1A1A1A] rounded-xl p-4">
                  <p className="text-sm text-neutral-300 font-body leading-relaxed">
                    In such cases, we do not provide a cash refund. Instead, we offer the option to <strong className="text-[#16A34A]">Reschedule</strong> your booking.
                  </p>
                </div>
              </div>
            </div>

            {/* Section 4 */}
            <div className="bg-[#111111] border border-[#1A1A1A] rounded-2xl p-6 md:p-8" data-testid="refund-section-4">
              <div className="flex items-start gap-3 mb-4">
                <span className="flex-shrink-0 w-7 h-7 rounded-lg bg-[#16A34A]/10 border border-[#16A34A]/20 flex items-center justify-center text-[11px] font-bold text-[#16A34A]">4</span>
                <h2 className="text-base md:text-lg font-bold font-heading pt-0.5">Rescheduling Process & Pricing</h2>
              </div>
              <div className="pl-10">
                <p className="text-sm text-neutral-400 font-body leading-relaxed mb-4">
                  To reschedule due to a turf-side issue, please follow these rules:
                </p>
                <div className="space-y-3">
                  <div className="bg-[#0A0A0A] border border-[#1A1A1A] rounded-xl p-4">
                    <div className="text-[10px] text-[#16A34A] uppercase tracking-wider font-semibold mb-1.5 font-heading">Contact Us</div>
                    <p className="text-sm text-neutral-400 font-body leading-relaxed">You must contact Malda Turf management directly to arrange a new slot.</p>
                  </div>
                  <div className="bg-[#0A0A0A] border border-[#1A1A1A] rounded-xl p-4">
                    <div className="text-[10px] text-[#16A34A] uppercase tracking-wider font-semibold mb-1.5 font-heading">Pricing Rules</div>
                    <ul className="space-y-2">
                      <li className="flex items-start gap-2.5">
                        <div className="w-1.5 h-1.5 rounded-full bg-[#16A34A]/50 flex-shrink-0 mt-1.5"></div>
                        <span className="text-sm text-neutral-400 font-body leading-relaxed">If the new slot price matches your original payment, the reschedule is <strong className="text-white">free</strong>.</span>
                      </li>
                      <li className="flex items-start gap-2.5">
                        <div className="w-1.5 h-1.5 rounded-full bg-[#16A34A]/50 flex-shrink-0 mt-1.5"></div>
                        <span className="text-sm text-neutral-400 font-body leading-relaxed">If you choose a slot with a higher tariff (e.g., moving from a weekday morning to a weekend night), the <strong className="text-white">difference in amount</strong> must be paid by the user.</span>
                      </li>
                      <li className="flex items-start gap-2.5">
                        <div className="w-1.5 h-1.5 rounded-full bg-[#16A34A]/50 flex-shrink-0 mt-1.5"></div>
                        <span className="text-sm text-neutral-400 font-body leading-relaxed">No refunds or credits will be given if the new slot is cheaper than the original.</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Section 5 - Contact */}
            <div className="bg-[#111111] border border-[#1A1A1A] rounded-2xl p-6 md:p-8" data-testid="refund-section-5">
              <div className="flex items-start gap-3 mb-4">
                <span className="flex-shrink-0 w-7 h-7 rounded-lg bg-[#16A34A]/10 border border-[#16A34A]/20 flex items-center justify-center text-[11px] font-bold text-[#16A34A]">5</span>
                <h2 className="text-base md:text-lg font-bold font-heading pt-0.5">Contact Information</h2>
              </div>
              <div className="pl-10">
                <p className="text-sm text-neutral-400 font-body leading-relaxed mb-4">
                  For queries regarding bookings affected by technical or weather issues, please contact us at:
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <a href="https://maldaturf.in" className="bg-[#0A0A0A] border border-[#1A1A1A] rounded-xl p-4 hover:border-[#262626] transition-colors group">
                    <Globe className="h-4 w-4 text-[#16A34A] mb-2" />
                    <div className="text-[10px] text-neutral-600 font-body mb-0.5">Website</div>
                    <div className="text-[12px] text-neutral-300 font-body group-hover:text-white transition-colors">maldaturf.in</div>
                  </a>
                  <a href="tel:+918918695356" className="bg-[#0A0A0A] border border-[#1A1A1A] rounded-xl p-4 hover:border-[#262626] transition-colors group">
                    <Phone className="h-4 w-4 text-[#16A34A] mb-2" />
                    <div className="text-[10px] text-neutral-600 font-body mb-0.5">Phone / WhatsApp</div>
                    <div className="text-[12px] text-neutral-300 font-body group-hover:text-white transition-colors">+91 89186 95356</div>
                  </a>
                  <a href="mailto:maldaturf@gmail.com" className="bg-[#0A0A0A] border border-[#1A1A1A] rounded-xl p-4 hover:border-[#262626] transition-colors group">
                    <Mail className="h-4 w-4 text-[#16A34A] mb-2" />
                    <div className="text-[10px] text-neutral-600 font-body mb-0.5">Email</div>
                    <div className="text-[12px] text-neutral-300 font-body group-hover:text-white transition-colors">maldaturf@gmail.com</div>
                  </a>
                </div>
              </div>
            </div>

          </div>

          {/* Footer note */}
          <div className="mt-10 text-center">
            <p className="text-[11px] text-neutral-600 font-body">
              By booking a slot on Malda Turf, you acknowledge and agree to the terms outlined above.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default RefundPage;
